import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import useFocusTrap from "../../../../hooks/useFocusTrap";
import "./index.css";

/**
 * Centered overlay container rendered in the global portal.
 * Close affordances are enabled by default; guarded flows can opt out while busy.
 */
const PopupOverlay = ({
    children,
    onClose,
    closeOnOverlayClick = true,
    closeOnEsc = true,
    zIndex,
    ariaLabel,
    ariaLabelledBy,
    trapFocus = true,
    restoreFocus = true,
    "aria-label": ariaLabelProp,
    "aria-labelledby": ariaLabelledByProp,
}) => {
    const dialogRef = useRef(null);
    const layerStyle = zIndex === undefined || zIndex === null
        ? undefined
        : { "--popup-overlay-z-index": String(zIndex) };
    const resolvedAriaLabel = ariaLabelProp || ariaLabel;
    const resolvedAriaLabelledBy = ariaLabelledByProp || ariaLabelledBy;
    const shouldCloseOnOverlayClick = closeOnOverlayClick && typeof onClose === "function";
    const shouldCloseOnEsc = closeOnEsc && typeof onClose === "function";
    const handleContainerClick = shouldCloseOnOverlayClick
        ? (event) => {
            if (event.target === event.currentTarget) {
                onClose();
            }
        }
        : undefined;

    useFocusTrap({
        active: trapFocus,
        containerRef: dialogRef,
        restoreFocus,
    });

    useEffect(() => {
        if (!shouldCloseOnEsc) return undefined;
        const handler = (e) => {
            if (e.key === "Escape") {
                onClose();
            }
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [shouldCloseOnEsc, onClose]);

    return (
        <>
            <div
                className="popup-container-overlay"
                role="presentation"
                onClick={shouldCloseOnOverlayClick ? onClose : undefined}
                style={layerStyle}
            />
            <div
                ref={dialogRef}
                className="popup-container"
                role="dialog"
                aria-modal="true"
                aria-label={resolvedAriaLabel}
                aria-labelledby={resolvedAriaLabelledBy}
                style={layerStyle}
                onClick={handleContainerClick}
            >
                {children}
            </div>
        </>
    );
};

PopupOverlay.propTypes = {
    children: PropTypes.node.isRequired,
    onClose: PropTypes.func,
    closeOnOverlayClick: PropTypes.bool,
    closeOnEsc: PropTypes.bool,
    zIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ariaLabel: PropTypes.string,
    ariaLabelledBy: PropTypes.string,
    trapFocus: PropTypes.bool,
    restoreFocus: PropTypes.bool,
    "aria-label": PropTypes.string,
    "aria-labelledby": PropTypes.string,
};

export default PopupOverlay;
